import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ContentCard from '@/components/base/ContentCard';
import SectionHeading from '@/components/base/SectionHeading';
import { topics } from '@/mocks/community';
import { getHotArticles, searchArticles } from '@/utils/content';
import { usePageMeta } from '@/hooks/usePageMeta';

const typeOptions = [
  { key: 'all', label: '全部' },
  { key: 'articles', label: '文章' },
  { key: 'topics', label: '社区话题' },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const queryParam = params.get('q') ?? '';
  const [keyword, setKeyword] = useState(queryParam);
  const [type, setType] = useState('all');

  usePageMeta(
    queryParam ? `搜索：${queryParam} - 鲁啊鲁` : '搜索 - 鲁啊鲁多元内容平台',
    queryParam
      ? `鲁啊鲁全站搜索「${queryParam}」的相关文章与社区话题结果。`
      : '在鲁啊鲁全站搜索资讯文章与社区话题。',
  );

  useEffect(() => {
    setKeyword(queryParam);
  }, [queryParam]);

  const matchedArticles = useMemo(() => searchArticles(queryParam), [queryParam]);
  const matchedTopics = useMemo(() => {
    const term = queryParam.trim().toLowerCase();
    if (!term) return [];
    return topics.filter((topic) =>
      [topic.title, topic.author, ...topic.tags, ...topic.content]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [queryParam]);

  const totalCount = matchedArticles.length + matchedTopics.length;
  const hotArticles = getHotArticles(4);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const term = keyword.trim();
    setParams(term ? { q: term } : {});
  };

  const showArticles = type === 'all' || type === 'articles';
  const showTopics = type === 'all' || type === 'topics';

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />

      <div className="mt-5 rounded-lg bg-primary-50 border border-primary-100 px-6 py-7 md:px-9 md:py-8">
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">全站搜索</h1>
        <p className="mt-1.5 text-sm text-foreground-600">在资讯与社区话题中查找你感兴趣的内容。</p>
        <form onSubmit={handleSearch} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="flex-1 flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2.5 focus-within:border-primary-400 transition-colors">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="输入关键词，如：科技、旅行、收纳"
              className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
            />
          </div>
          <button
            type="submit"
            className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
          >
            搜索
          </button>
        </form>
      </div>

      {queryParam ? (
        <>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-foreground-500">
              关键词「<span className="font-semibold text-foreground-900">{queryParam}</span>」共找到{' '}
              <span className="font-semibold text-foreground-900">{totalCount}</span> 条结果
            </p>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1 w-fit">
              {typeOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setType(option.key)}
                  className={`px-4 py-1.5 text-sm rounded-full whitespace-nowrap transition-colors ${
                    type === option.key
                      ? 'bg-background-50 text-primary-700 font-medium'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {totalCount === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 py-14 text-center">
              <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h2 className="text-lg font-bold text-foreground-900">没有找到相关内容</h2>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词试试，或者看看下面的热门内容。
              </p>
            </div>
          ) : (
            <div className="mt-6 space-y-10">
              {showArticles && matchedArticles.length > 0 ? (
                <section>
                  <SectionHeading
                    title="文章"
                    subtitle={`共 ${matchedArticles.length} 篇`}
                    icon="ri-article-line"
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {matchedArticles.map((article) => (
                      <ContentCard key={article.id} article={article} variant="horizontal" />
                    ))}
                  </div>
                </section>
              ) : null}

              {showTopics && matchedTopics.length > 0 ? (
                <section>
                  <SectionHeading
                    title="社区话题"
                    subtitle={`共 ${matchedTopics.length} 个`}
                    icon="ri-chat-smile-2-line"
                  />
                  <div className="space-y-3">
                    {matchedTopics.map((topic) => (
                      <Link
                        key={topic.id}
                        to="/community"
                        className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
                      >
                        <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold">
                          {topic.avatar}
                        </span>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-700 transition-colors">
                            {topic.title}
                          </h3>
                          <p className="mt-1 text-xs text-foreground-400">
                            {topic.author} · {topic.replies} 回复 · {topic.updatedAt}
                          </p>
                        </div>
                        <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
                      </Link>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </>
      ) : (
        <div className="mt-6 rounded-lg border border-dashed border-background-300 py-14 text-center">
          <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-search-line text-3xl"></i>
          </span>
          <h2 className="text-lg font-bold text-foreground-900">开启一次搜索吧</h2>
          <p className="mt-2 text-sm text-foreground-500">输入关键词，发现资讯与社区里的精彩内容。</p>
        </div>
      )}

      <section className="mt-14">
        <SectionHeading title="热门内容推荐" subtitle="不知道看什么？从这些开始" icon="ri-fire-line" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {hotArticles.map((article) => (
            <ContentCard key={article.id} article={article} />
          ))}
        </div>
      </section>
    </div>
  );
}